import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./ui/button";
import {
  Crown, X, Check, Sparkle, PencilLine, ChartLineUp, Headphones,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEATURES = [
  { icon: Sparkle, text: "AI-powered Coach KB journal reflections" },
  { icon: ChartLineUp, text: "Advanced mood pattern insights & analytics" },
  { icon: PencilLine, text: "Journal theme analysis & word tracking" },
  { icon: Headphones, text: "Unlimited sensory reset sessions" },
];

export default function PremiumUpgrade({ isOpen, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      const origin = window.location.origin;
      const res = await axios.post(
        `${API}/payments/checkout`,
        { origin_url: origin },
        { withCredentials: true }
      );
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      console.error("Checkout failed:", err);
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          data-testid="premium-upgrade-modal"
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />

          {/* Modal */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            className="relative bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden"
          >
            {/* Header */}
            <div className="bg-[#2D6A4F] p-8 text-center relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 rounded-full bg-[#84A98C] blur-[60px] opacity-20" />
              <div className="absolute bottom-0 left-0 w-24 h-24 rounded-full bg-[#CDB4DB] blur-[50px] opacity-15" />

              <button
                onClick={onClose}
                className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white/70 hover:bg-white/20 transition-all"
                data-testid="close-premium-modal"
              >
                <X size={16} weight="bold" />
              </button>

              <Crown size={40} weight="duotone" className="text-[#F4A261] mx-auto mb-3" />
              <h2 className="font-['Outfit'] text-2xl font-medium text-white mb-1">
                Unlock Premium
              </h2>
              <p className="text-white/70 text-sm">
                Deepen your wellness journey with AI-powered insights
              </p>
            </div>

            {/* Body */}
            <div className="p-8">
              {/* Price */}
              <div className="text-center mb-6">
                <div className="flex items-baseline justify-center gap-1">
                  <span className="font-['Outfit'] text-4xl font-medium text-[#1A2421]">$14.99</span>
                  <span className="text-sm text-[#78857F]">/month</span>
                </div>
              </div>

              {/* Features */}
              <div className="space-y-3 mb-8">
                {FEATURES.map((f, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 + i * 0.08 }}
                    className="flex items-center gap-3"
                  >
                    <div className="w-8 h-8 rounded-lg bg-[#2D6A4F]/8 flex items-center justify-center shrink-0">
                      <f.icon size={16} weight="duotone" className="text-[#2D6A4F]" />
                    </div>
                    <span className="text-sm text-[#1A2421]">{f.text}</span>
                  </motion.div>
                ))}
              </div>

              <Button
                onClick={handleUpgrade}
                disabled={loading}
                data-testid="upgrade-premium-button"
                className="w-full rounded-full h-12 bg-[#2D6A4F] hover:bg-[#1B4936] text-white font-medium transition-all hover:-translate-y-0.5 shadow-lg"
              >
                {loading ? "Redirecting to checkout..." : "Upgrade to Premium"}
              </Button>

              <p className="text-center text-[10px] text-[#78857F] mt-4">
                Secure payment via Stripe. Cancel anytime.
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/** Small inline badge/button for premium-locked features */
export function PremiumBadge({ onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid="premium-badge"
      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#F4A261]/12 text-[#F4A261] hover:bg-[#F4A261]/20 transition-all"
    >
      <Crown size={12} weight="fill" />
      Premium
    </button>
  );
}
